<template>
    <el-form class="booking-form" label-position="top">
        <el-form-item>
            <v-select
                :disabled="readonlyFields.includes('xcube_id')"
                v-model="booking.xcube_id"
                :options="allXCubes"
                :reduce="optionId"
                label="name"
                :clearable="false"
                placeholder="Active X-Cube"
                @input="updateDevice"
            />
        </el-form-item>
        <el-form-item v-if="booking.xcube_id" label="Experience">
            <v-select
                :disabled="readonlyFields.includes('experience_id')"
                v-model="booking.experience_id"
                :options="experiences"
                :reduce="optionId"
                label="name"
                :clearable="false"
                placeholder="Experience"
                @input="updateOptions"
            />
        </el-form-item>
        <div v-if="booking.experience_id">
            <el-form-item label="Amount of Players">
                <v-select
                    v-model="booking.team_1_size"
                    :options="playerOptions"
                    :disabled="readonlyFields.includes('team_1_size')"
                    :clearable="false"
                />
            </el-form-item>


            <el-form-item label="Name">
                <el-input :disabled="readonlyFields.includes('name')" placeholder="John Doe" v-model="booking.name" required />
            </el-form-item>
            <el-form-item label="Email">
                <el-input :disabled="readonlyFields.includes('email')" placeholder="john@doe.com" type="email" v-model="booking.email" required />
            </el-form-item>
            <el-form-item label="Mobile">
                <el-input :disabled="readonlyFields.includes('phone_number')" placeholder="+31 6 12345678" type="tel" v-model="booking.phone_number" />
            </el-form-item>
            <el-form-item label="Teamname">
                <el-input :disabled="readonlyFields.includes('team_1_name')" placeholder="Team Fam Doe" v-model="booking.team_1_name" required />
            </el-form-item>
            <el-form-item label="Language">
                <v-select
                    :disabled="readonlyFields.includes('language')"
                    v-model="booking.language"
                    :options="languageOptions"
                    :clearable="false"
                />
            </el-form-item>
            <el-form-item label="Event Date">
                <div class="input-with-text">
                    <date-picker :disabled="readonlyFields.includes('eventDate')" v-model="eventDate" type="date" format="DD-MM-YYYY"
                        :disabled-date="(date) => date < dayjs().startOf('day').toDate()" :get-classes="closedDayClass"
                        @change="updateOptions"
                        placeholder="Select date" required>
                        <template #footer>
                            <span>Marked days are outside opening hours.</span>
                        </template>
                    </date-picker>
                </div>
                <span v-if="selectedDayOutside" class="text-next-to-input">This day is outside the location's opening hours. You can still book it with an Event Time below.</span>
            </el-form-item>
            <el-form-item v-if="experience.duration" label="Event Time">
                <select-time v-model="eventTime" :disabled="readonlyFields.includes('eventDate')" :time-slots="slotsWithStatus" :duration="experience.duration" :outside-opening-hours="selectedTimeOutside" :day-outside-opening-hours="selectedDayOutside" />
                <span class="text-next-to-input">Timezone: {{ locationTimeZone }}</span>
            </el-form-item>
            <el-form-item v-if="eventDateTime && locationTimeZone !== userTimeZone" label="Local Time">
                <span class="text-next-to-input">
                    {{ dayjs(eventDateTime).format('DD-MM-YYYY HH:mm') }}
                    {{ userTimeZone }}
                </span>
            </el-form-item>
            <slot></slot>
        </div>
    </el-form>
</template>

<script setup>
import { Message } from "element-ui";
import { computed, onMounted, ref, watch } from "vue";
import DatePicker from "vue2-datepicker";
import dayjs from "dayjs";
import * as Sentry from "@sentry/vue";
import SelectTime from "../shared/SelectTime.vue";
import "vue2-datepicker/index.css";
import { getLocationSchedule, getTimeSlots } from "../../api/rest";
import { calendarDateOf, dayIsOutsideOpeningHours, timeIsOutsideOpeningHours } from "../../helpers/openingHours";
import { geXCubesForLocations } from "../../services/xcubeService";
import { getExperiencesForDevice } from "../../services/experienceService";

const props = defineProps({
    locations: {
        type: Array,
        default: () => [],
    },
    value: {
        type: Object,
        required: true,
    },
    readonlyFields: {
        type: Array,
        default: () => [],
    },
});

const booking = computed({
    get: () => props.value,
    set: (val) => emit('input', val)
});


const emit = defineEmits(['input']);

const eventDate = ref(booking.value.event_date ? dayjs(booking.value.event_date).toDate() : new Date());
const eventTime = ref(booking.value.event_time ? dayjs(booking.value.event_time).format("HH:mm") : "");
const experiences = ref([]);
const playerOptions = ref([]);
const languageOptions = ref([]);
const timeSlots = ref([]);
const schedule = ref(null);

const slotsWithStatus = computed(() => timeSlots.value.map((slot) => {
    let status = "available";
    const [hours, minutes] = slot.time.split(":").map(Number);
    const slotDateTime = dayjs(eventDate.value).tz(locationTimeZone.value, true)
        .hour(hours)
        .minute(minutes)
        .second(0);
    if (slotDateTime.isBefore(dayjs())) {
        status = "past";
    } else if (slot.available === 0) {
        status = "unavailable";
    } else if (slot.time === eventTime.value) {
        status = "selected";
    }
    return {
        time: slot.time,
        status,
    };
}));

const updateDevice = async () => {
    const device = booking.value.xcube_id
    booking.value.experience_id = null;
    booking.value.team_1_size = null;
    booking.value.language = null;
    booking.value.xcube_id = device;
    await updateOptions();
};

const experience = computed(() => {
    return experiences.value.find((exp) => exp.id === booking.value.experience_id) || {};
});

const xcube = computed(() => {
    return allXCubes.value.find((x) => x.id === booking.value.xcube_id) || {};
});

const updateOptions = async () => {
    experiences.value = await getExperiencesForDevice(booking.value.xcube_id);
    const po = [];
    if (booking.value.experience_id) {
        const controllers = xcube.value.config?.devices?.totalControllerCount || 8;
        const minPlayers = experience.value?.min_number_of_players || 2;
        const maxPlayers = Math.min(controllers, experience.value?.max_number_of_players || 8);
        if (experience.value) {
            for (let i = minPlayers; i <= maxPlayers; i++) {
                po.push(i);
            }
            if (!booking.value.team_1_size || booking.value.team_1_size < minPlayers) {
                booking.value.team_1_size = minPlayers;
            }
            if (booking.value.team_1_size > maxPlayers) {
                booking.value.team_1_size = maxPlayers;
            }
            languageOptions.value = experience.value.config?.availableLanguages || ["en"];
            if (!languageOptions.value.includes(booking.value.language)) {
                booking.value.language = languageOptions.value[0];
            }
        }

        let newTimeSlots = [];

        if (eventDate.value) {
            booking.value.event_date = eventDate.value;
            const { json, status } = await getTimeSlots(
                booking.value.xcube_id,
                dayjs(eventDate.value).format("YYYY-MM-DD"),
                experience.value.duration,
            )
            if (status !== 200) {
                Message.error({
                    message: `Error from server : ${json.message}`,
                });
                return;
            }
            newTimeSlots = json;
        }
        timeSlots.value = newTimeSlots;
        booking.value.event_date = eventDate.value;
    }
    playerOptions.value = po;
};

const selectedLocation = computed(() => props.locations
    .find((location) => location.xcubes.some((x) => x.id === booking.value.xcube_id)));

const locationTimeZone = computed(() => selectedLocation.value?.time_zone || userTimeZone.value);

const selectedDayOutside = computed(() => {
    if (!schedule.value || !eventDate.value || !selectedLocation.value?.time_zone) {
        return false;
    }
    return dayIsOutsideOpeningHours(
        schedule.value,
        calendarDateOf(eventDate.value),
        selectedLocation.value.time_zone,
    );
});

const selectedTimeOutside = computed(() => {
    if (selectedDayOutside.value || !schedule.value || !eventTime.value || !selectedLocation.value?.time_zone) {
        return false;
    }
    return timeIsOutsideOpeningHours(schedule.value, {
        date: calendarDateOf(eventDate.value),
        time: eventTime.value,
        duration: experience.value.duration,
        timeZone: selectedLocation.value.time_zone,
    });
});

const closedDayClass = (date) => {
    if (!schedule.value || !selectedLocation.value?.time_zone) {
        return [];
    }
    return dayIsOutsideOpeningHours(schedule.value, calendarDateOf(date), selectedLocation.value.time_zone)
        ? ["hub-closed-day"]
        : [];
};

let scheduleRequest = 0;
const loadSchedule = async (locationId) => {
    const request = ++scheduleRequest;
    if (!locationId) {
        schedule.value = null;
        return;
    }
    try {
        const next = await getLocationSchedule(locationId);
        if (request === scheduleRequest) {
            schedule.value = next;
        }
    } catch (error) {
        if (request !== scheduleRequest) {
            return;
        }
        schedule.value = null;
        Message.error("Could not load opening hours");
        Sentry.captureException(error);
    }
};

watch(() => selectedLocation.value?.id, (locationId) => {
    loadSchedule(locationId);
}, { immediate: true });

const allXCubes = computed(() => geXCubesForLocations(props.locations));

const optionId = (item) => item.id;

const eventDateTime = computed(() => {
    if (eventDate.value && eventTime.value) {
        const [hours, minutes] = eventTime.value.split(":");
        const hour = Number.parseInt(hours);
        const minute = Number.parseInt(minutes);
        return dayjs(eventDate.value).tz(locationTimeZone.value).hour(hour).minute(minute).second(0).toDate();
    }
    return null;
});

const userTimeZone = computed(() => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
        Sentry.captureException(error);
        return "Europe/Amsterdam";
    }
});


onMounted(async () => {
    if (booking.value.experience_id) {
        await updateOptions();
    }
});

watch(eventDateTime, (newDateTime) => {
    booking.value.event_datetime = newDateTime;
});

</script>

<style lang="scss" scoped>
@use "../../colors" as *;


.booking-form {
    box-sizing: border-box;
    width: 100%;
    margin: 0 0 2rem;
}

.text-next-to-input {
    color: $color-primary;
    text-align: left;
    display: block;
}

.input-with-text {
    display: flex;
}

</style>